import { RightOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';

import type { HierarchyNodeData } from './HierarchyNavigator';
import { hierarchyNodeUtils } from './utils';

interface HierarchyNodeProps {
  node: HierarchyNodeData;
  onNodeSelect: (node: HierarchyNodeData) => void;
  selectedNodeId?: string;
  level: number;
  searchValue: string;
  initialOpen?: boolean;
}

const HierarchyNode = ({
  node,
  onNodeSelect,
  selectedNodeId,
  level,
  searchValue,
  initialOpen,
}: HierarchyNodeProps) => {
  const [isExpanded, setIsExpanded] = useState(initialOpen);
  const color = useColor();
  const isSelected = selectedNodeId === node.id;
  const hasChildren = Boolean(node.children && node.children.length > 0);

  const handleNodeClick = () => {
    onNodeSelect(node);
    setIsExpanded(true);
  };

  const handleExpandClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const highlightText = (text: string) => {
    if (!searchValue.trim()) return text;

    const parts = text.split(new RegExp(`(${searchValue})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === searchValue.toLowerCase() ? (
        <mark key={index} style={{ backgroundColor: color.yellow500 }}>
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  useEffect(() => {
    if (searchValue) {
      setIsExpanded(true);
    }
  }, [searchValue]);

  return (
    <VFlex>
      <NodeContainer
        a="center"
        g={2}
        isSelected={isSelected}
        level={level}
        onClick={handleNodeClick}
      >
        {/* Expand/Collapse Icon */}
        {hasChildren ? (
          <IconButton
            onClick={handleExpandClick}
            outlined={false}
            icon={
              <RightOutlined
                style={{
                  fontSize: '12px',
                  color: color.gray800,
                  transition: 'transform 0.2s',
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
              />
            }
            style={{ backgroundColor: 'transparent' }}
          />
        ) : (
          <div style={{ width: '24px', aspectRatio: 1 }} />
        )}

        {/* Node Content */}
        <Flex f={1} j="space-between" a="center">
          <Typo
            type="label2"
            weight="sb"
            style={{ color: isSelected ? color.gray800 : color.gray700 }}
            ellipsis
          >
            {highlightText(node.name)}
          </Typo>
        </Flex>
      </NodeContainer>

      {/* Children */}
      {hasChildren && isExpanded && (
        <VFlex g={4}>
          {node.children!.map((child) => {
            const initialOpen =
              child.id === selectedNodeId ||
              hierarchyNodeUtils
                .getAllChildren(child)
                .some((grandChild) => grandChild.id === selectedNodeId);
            return (
              <HierarchyNode
                key={child.id}
                node={child}
                onNodeSelect={onNodeSelect}
                selectedNodeId={selectedNodeId}
                level={level + 1}
                searchValue={searchValue}
                initialOpen={initialOpen}
              />
            );
          })}
        </VFlex>
      )}
    </VFlex>
  );
};

const NodeContainer = styled(Flex)<{
  isSelected: boolean;
  level: number;
}>`
  padding: 0px 12px;
  height: 48px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  margin-left: ${(props) => props.level * 16}px;

  background-color: ${(props) =>
    props.isSelected ? props.theme.colors.selected : 'transparent'};

  border: 1px solid
    ${(props) =>
      props.isSelected ? props.theme.colors.primary100 : 'transparent'};

  &:hover {
    background-color: ${(props) => props.theme.colors.hover};
  }
`;

export default HierarchyNode;
